import { useEffect, useState, useCallback, useMemo } from "react"
import {
    Plus,
    BookOpen,
    HelpCircle,
    Home,
    FolderOpen,
    Users,
    Clock,
    BarChart3,
    History,
    UserCircle,
    Play,
    Square,
    Timer,
    ChevronRight,
    Briefcase,
    Zap,
} from "lucide-react"
import { getAllArticles } from "@/lib/help-content"
import { useTranslation } from "react-i18next"
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
} from "@/components/ui/command"
import { useNavigate } from "react-router-dom"
import { useProjectsQuery } from "@/hooks/queries/use-projects"
import { useTimeTracking } from "@/hooks/use-time-tracking"
import { CreateProjectDialog } from "@/components/dialogs/create-project-dialog"

interface CommandMenuProps {
    open: boolean
    setOpen: (open: boolean) => void
}

export function CommandMenu({ open, setOpen }: CommandMenuProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { data: projects = [], isLoading: isLoadingProjects } = useProjectsQuery()
    const { activeEntry, clockIn, clockOut, isLoading: isTimerLoading } = useTimeTracking()
    const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false)
    const allArticles = getAllArticles(t)

    const NAV_ITEMS = useMemo(() => [
        { label: t('nav.home') || "Inicio", path: "/", icon: Home, shortcut: "G I", searchValue: "inicio home dashboard" },
        { label: t('nav.projects') || "Proyectos", path: "/projects", icon: FolderOpen, shortcut: "G P", searchValue: "proyectos projects" },
        { label: t('nav.clients') || "Clientes", path: "/clients", icon: Briefcase, shortcut: "G C", searchValue: "clientes clients" },
        { label: t('nav.team') || "Equipo", path: "/team", icon: Users, shortcut: "G E", searchValue: "equipo team members" },
        { label: t('nav.history') || "Historial", path: "/history", icon: History, shortcut: "G H", searchValue: "historial history registros" },
        { label: t('nav.reports') || "Reportes", path: "/reports", icon: BarChart3, shortcut: "G R", searchValue: "reportes reports analytics" },
        { label: t('nav.profile') || "Perfil", path: "/profile", icon: UserCircle, shortcut: "G U", searchValue: "perfil profile configuracion settings" },
        { label: t('nav.help') || "Centro de Ayuda", path: "/help", icon: HelpCircle, searchValue: "ayuda help soporte support" },
    ], [t])

    // Keyboard shortcut: Ctrl/Cmd+K
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen(!open)
            }
        }
        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [open, setOpen])

    const runCommand = useCallback((command: () => void) => {
        setOpen(false)
        command()
    }, [setOpen])

    const handleStartTimer = useCallback((projectId: string, taskId?: string) => {
        runCommand(() => clockIn(projectId, taskId))
    }, [runCommand, clockIn])

    const handleStopTimer = useCallback(() => {
        runCommand(() => clockOut())
    }, [runCommand, clockOut])

    const activeProjectName = activeEntry?.projects?.name || activeEntry?.project?.name || null

    return (
        <>
            <CommandDialog open={open} onOpenChange={setOpen}>
                <CommandInput placeholder={t('common.searchPlaceholder') || "Buscar acciones, proyectos, ayuda..."} />
                <CommandList className="max-h-[460px]">
                    <CommandEmpty>{t('common.noResults') || "No se encontraron resultados."}</CommandEmpty>

                    {/* ── Timer Actions ── */}
                    <CommandGroup heading="Timer">
                        {activeEntry ? (
                            <CommandItem
                                onSelect={handleStopTimer}
                                value="parar detener timer stop sesion activa"
                                disabled={isTimerLoading}
                                className="text-destructive data-[selected=true]:bg-destructive/10"
                            >
                                <Square className="mr-2 h-4 w-4 text-destructive" />
                                <div className="flex flex-col">
                                    <span>{t('timer.stopSession') || "Detener Sesión Activa"}</span>
                                    {activeProjectName && (
                                        <span className="text-[11px] text-muted-foreground">
                                            {t('timer.workingOn') || "Trabajando en"}: {activeProjectName}
                                        </span>
                                    )}
                                </div>
                            </CommandItem>
                        ) : (
                            <CommandItem
                                onSelect={() => runCommand(() => navigate("/"))}
                                value="iniciar timer empezar trabajar start"
                                className="text-primary data-[selected=true]:bg-primary/10"
                            >
                                <Play className="mr-2 h-4 w-4 text-primary" />
                                <span>{t('timer.startSession') || "Iniciar Sesión de Trabajo"}</span>
                                <CommandShortcut>
                                    <ChevronRight className="h-3 w-3" />
                                </CommandShortcut>
                            </CommandItem>
                        )}
                    </CommandGroup>

                    <CommandSeparator />

                    {/* ── Quick Actions ── */}
                    <CommandGroup heading={t('common.quickActions') || "Acciones Rápidas"}>
                        <CommandItem
                            onSelect={() => runCommand(() => setIsCreateProjectOpen(true))}
                            value="nuevo proyecto crear project new"
                        >
                            <Plus className="mr-2 h-4 w-4 text-primary" />
                            <span>{t('projects.createNew') || "Crear Nuevo Proyecto"}</span>
                            <CommandShortcut>⌘N</CommandShortcut>
                        </CommandItem>
                        <CommandItem
                            onSelect={() => runCommand(() => navigate("/reports"))}
                            value="ver reportes report analytics exportar"
                        >
                            <Zap className="mr-2 h-4 w-4 text-amber-500" />
                            <span>{t('nav.reports') || "Ver Reportes"}</span>
                        </CommandItem>
                        <CommandItem
                            onSelect={() => runCommand(() => navigate("/history"))}
                            value="ver historial history registros recientes"
                        >
                            <Clock className="mr-2 h-4 w-4 text-blue-500" />
                            <span>{t('nav.history') || "Ver Historial de Tiempo"}</span>
                        </CommandItem>
                    </CommandGroup>

                    <CommandSeparator />

                    {/* ── Navigation ── */}
                    <CommandGroup heading={t('nav.navigation') || "Navegación"}>
                        {NAV_ITEMS.map((item) => (
                            <CommandItem
                                key={item.path}
                                onSelect={() => runCommand(() => navigate(item.path))}
                                value={item.searchValue}
                            >
                                <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                                <span>{item.label}</span>
                                {item.shortcut && (
                                    <CommandShortcut>{item.shortcut}</CommandShortcut>
                                )}
                            </CommandItem>
                        ))}
                    </CommandGroup>

                    {/* ── Projects (dynamic) ── */}
                    {!isLoadingProjects && projects.length > 0 && (
                        <>
                            <CommandSeparator />
                            <CommandGroup heading={t('projects.myProjects') || "Mis Proyectos"}>
                                {projects.slice(0, 8).map((project) => {
                                    const isActive = activeEntry?.project_id === project.id
                                    return (
                                        <CommandItem
                                            key={project.id}
                                            onSelect={() => {
                                                if (isActive) return
                                                handleStartTimer(project.id)
                                            }}
                                            value={`proyecto ${project.name} iniciar trabajar`}
                                            disabled={isTimerLoading}
                                        >
                                            <span
                                                className="mr-2 h-3 w-3 rounded-full shrink-0 ring-1 ring-black/10"
                                                style={{ backgroundColor: project.color || '#6366f1' }}
                                            />
                                            <span className="truncate">{project.name}</span>
                                            {isActive ? (
                                                <CommandShortcut className="flex items-center gap-1 text-primary">
                                                    <Timer className="h-3 w-3 animate-pulse" />
                                                    {t('common.active') || "Activo"}
                                                </CommandShortcut>
                                            ) : (
                                                <CommandShortcut className="flex items-center gap-1">
                                                    <Play className="h-3 w-3" />
                                                </CommandShortcut>
                                            )}
                                        </CommandItem>
                                    )
                                })}
                                {projects.length > 8 && (
                                    <CommandItem
                                        onSelect={() => runCommand(() => navigate("/projects"))}
                                        value="ver todos proyectos"
                                        className="text-muted-foreground"
                                    >
                                        <FolderOpen className="mr-2 h-4 w-4" />
                                        <span>{t('projects.viewAll', { count: projects.length }) || `Ver todos los proyectos (${projects.length})`}</span>
                                    </CommandItem>
                                )}
                            </CommandGroup>
                        </>
                    )}

                    {/* ── Help Articles ── */}
                    {allArticles.length > 0 && (
                        <>
                            <CommandSeparator />
                            <CommandGroup heading={t('nav.help') || "Centro de Ayuda"}>
                                {allArticles.map((article) => (
                                    <CommandItem
                                        key={article.id}
                                        onSelect={() => runCommand(() => navigate(`/help/${article.category}/${article.id}`))}
                                        value={`${article.title} ${article.description} ayuda help`}
                                    >
                                        <BookOpen className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-sm truncate">{article.title}</span>
                                            <span className="text-[11px] text-muted-foreground line-clamp-1">
                                                {article.description}
                                            </span>
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </>
                    )}
                </CommandList>

                {/* Footer hint */}
                <div className="flex items-center justify-between border-t px-3 py-2">
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border bg-muted px-1 font-mono">↑↓</kbd> {t('common.navigate') || "navegar"}
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border bg-muted px-1 font-mono">↵</kbd> {t('common.select') || "seleccionar"}
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border bg-muted px-1 font-mono">esc</kbd> {t('common.close') || "cerrar"}
                        </span>
                    </div>
                </div>
            </CommandDialog>

            {/* Dialog montado fuera del CommandDialog para evitar conflictos de foco */}
            <CreateProjectDialog
                open={isCreateProjectOpen}
                onOpenChange={setIsCreateProjectOpen}
                onSuccess={() => setIsCreateProjectOpen(false)}
            />
        </>
    )
}
